import type { Action, ValidationResult } from "../../types/actions";
import type { Project } from "../../types/project";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler } from "../registry";
import { findClip, syncNestedInstance } from "./clip-helpers";

/**
 * The four trim operations that move a clip's timing without adding or removing
 * anything. They lived in the executor's switch with no case in either the
 * validator or the inverse generator, so they applied unchecked and could not be
 * undone: the executor still pushed a history entry, undo popped it onto the
 * redo stack, found a null inverse and reported failure, and the edit stayed.
 *
 * They all invert the same way, by putting back the timing fields of whichever
 * clips they touched, so they share one inverse type rather than each growing an
 * arithmetic inverse that has to reason about the clamps below.
 */

interface ClipTiming {
  clipId: string;
  startTime: number;
  duration: number;
  inPoint: number;
  outPoint: number;
}

const ok = (): ValidationResult => ({ valid: true, errors: [] });
const err = (message: string, path?: string): ValidationResult => ({
  valid: false,
  errors: [{ code: "INVALID_PARAMS", message, path }],
});

function timingOf(project: Project, clipId: string): ClipTiming | null {
  const clip = findClip(project, clipId);
  if (!clip) return null;
  return {
    clipId,
    startTime: clip.startTime,
    duration: clip.duration,
    inPoint: clip.inPoint,
    outPoint: clip.outPoint,
  };
}

function restoreFor(
  action: Action,
  projectBefore: Project,
  clipIds: Array<string | undefined>,
): Action | null {
  const clips = clipIds
    .filter((id): id is string => typeof id === "string" && id.length > 0)
    .map((id) => timingOf(projectBefore, id))
    .filter((t): t is ClipTiming => t !== null);
  if (clips.length === 0) return null;
  return createInverseAction(action, "clip/restoreTiming", { clips });
}

function mapClips(
  project: Project,
  fn: (clip: MutableClip) => MutableClip,
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
    ...track,
    clips: track.clips.map(fn),
  }));
}

function requireClip(clipId: unknown, project: Project): ValidationResult {
  if (typeof clipId !== "string" || !clipId) {
    return err("Clip ID is required and must be a string", "params.clipId");
  }
  return findClip(project, clipId)
    ? ok()
    : err(`Clip with ID ${clipId} not found`, "params.clipId");
}

function requireDelta(delta: unknown): ValidationResult | null {
  return typeof delta === "number" && Number.isFinite(delta)
    ? null
    : err("delta must be a finite number", "params.delta");
}

const clipRestoreTiming: ActionHandler = {
  type: "clip/restoreTiming",
  validate(action: Action): ValidationResult {
    const clips = (action.params as { clips?: unknown }).clips;
    return Array.isArray(clips) && clips.length > 0
      ? ok()
      : err("clip/restoreTiming requires a non-empty clips array", "params.clips");
  },
  apply(action: Action, project: Project): void {
    const { clips } = action.params as { clips: ClipTiming[] };
    const byId = new Map(clips.map((c) => [c.clipId, c]));
    mapClips(project, (clip) => {
      const timing = byId.get(clip.id);
      if (!timing) return clip;
      return {
        ...clip,
        startTime: timing.startTime,
        duration: timing.duration,
        inPoint: timing.inPoint,
        outPoint: timing.outPoint,
      };
    });
    for (const c of clips) syncNestedInstance(project, c.clipId);
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const { clips } = action.params as { clips: ClipTiming[] };
    return restoreFor(action, projectBefore, clips.map((c) => c.clipId));
  },
};

const clipSlip: ActionHandler = {
  type: "clip/slip",
  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown; delta?: unknown };
    const clip = requireClip(params.clipId, project);
    return clip.valid ? (requireDelta(params.delta) ?? ok()) : clip;
  },
  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; delta: number };
    const clip = findClip(project, params.clipId);
    if (!clip) return;
    const newInPoint = Math.max(0, clip.inPoint + params.delta);
    const newOutPoint = newInPoint + clip.duration;
    mapClips(project, (c) =>
      c.id === params.clipId
        ? { ...c, inPoint: newInPoint, outPoint: newOutPoint }
        : c,
    );
    syncNestedInstance(project, params.clipId);
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    return restoreFor(action, projectBefore, [params.clipId]);
  },
};

const clipSlide: ActionHandler = {
  type: "clip/slide",
  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown; delta?: unknown };
    const clip = requireClip(params.clipId, project);
    return clip.valid ? (requireDelta(params.delta) ?? ok()) : clip;
  },
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      delta: number;
      prevClipId?: string;
      nextClipId?: string;
    };
    mapClips(project, (c) => {
      if (c.id === params.clipId) {
        return { ...c, startTime: Math.max(0, c.startTime + params.delta) };
      }
      if (c.id === params.prevClipId && params.delta > 0) {
        return { ...c, duration: c.duration + params.delta };
      }
      if (c.id === params.nextClipId && params.delta < 0) {
        return {
          ...c,
          startTime: c.startTime + params.delta,
          inPoint: c.inPoint - params.delta,
          duration: c.duration - params.delta,
        };
      }
      return c;
    });
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as {
      clipId: string;
      prevClipId?: string;
      nextClipId?: string;
    };
    // Both neighbours go in even though a given delta only moves one of them:
    // restoring a clip to the timing it already has is a no-op, and guessing
    // from the sign here would have to stay in step with apply forever.
    return restoreFor(action, projectBefore, [
      params.clipId,
      params.prevClipId,
      params.nextClipId,
    ]);
  },
};

const clipRoll: ActionHandler = {
  type: "clip/roll",
  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      leftClipId?: unknown;
      rightClipId?: unknown;
      delta?: unknown;
    };
    for (const id of [params.leftClipId, params.rightClipId]) {
      if (typeof id !== "string" || !findClip(project, id)) {
        return err(`Clip with ID ${String(id)} not found`, "params");
      }
    }
    return requireDelta(params.delta) ?? ok();
  },
  apply(action: Action, project: Project): void {
    const params = action.params as {
      leftClipId: string;
      rightClipId: string;
      delta: number;
    };
    mapClips(project, (c) => {
      if (c.id === params.leftClipId) {
        return {
          ...c,
          duration: c.duration + params.delta,
          outPoint: c.outPoint + params.delta,
        };
      }
      if (c.id === params.rightClipId) {
        return {
          ...c,
          startTime: c.startTime + params.delta,
          inPoint: c.inPoint + params.delta,
          duration: c.duration - params.delta,
        };
      }
      return c;
    });
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { leftClipId: string; rightClipId: string };
    return restoreFor(action, projectBefore, [
      params.leftClipId,
      params.rightClipId,
    ]);
  },
};

const clipTrimToPlayhead: ActionHandler = {
  type: "clip/trimToPlayhead",
  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      clipId?: unknown;
      playheadTime?: unknown;
    };
    const clip = requireClip(params.clipId, project);
    if (!clip.valid) return clip;
    return typeof params.playheadTime === "number" &&
      Number.isFinite(params.playheadTime)
      ? ok()
      : err("playheadTime must be a finite number", "params.playheadTime");
  },
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      playheadTime: number;
      trimStart: boolean;
    };
    if (!findClip(project, params.clipId)) return;
    mapClips(project, (c) => {
      if (c.id !== params.clipId) return c;
      if (params.trimStart) {
        const delta = params.playheadTime - c.startTime;
        return {
          ...c,
          startTime: params.playheadTime,
          inPoint: c.inPoint + delta,
          duration: c.duration - delta,
        };
      }
      return {
        ...c,
        duration: params.playheadTime - c.startTime,
        outPoint: c.inPoint + (params.playheadTime - c.startTime),
      };
    });
    syncNestedInstance(project, params.clipId);
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    return restoreFor(action, projectBefore, [params.clipId]);
  },
};

for (const handler of [
  clipRestoreTiming,
  clipSlip,
  clipSlide,
  clipRoll,
  clipTrimToPlayhead,
]) {
  registerActionHandler(handler);
}
